/**
 * Batch List Component
 *
 * Displays inventory batches for a specific product with FIFO ordering,
 * cost tracking, and batch-level actions (adjust quantity, change status).
 *
 * @module pages/inventory/BatchList
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Form,
  Dropdown,
  Spinner,
  Modal,
  Alert,
} from 'react-bootstrap';
import {
  getInventoryBatches,
  getInventoryItem,
  adjustBatch,
} from '../../services/inventoryService';
import { listProjects } from '../../services/projectService';
import { supabase } from '../../services/supabase';
import { PROJECT_STATUS } from '../../utils/constants';
import KebabMenu from '../../components/common/KebabMenu';
import { formatCurrency, formatDate, formatNumber } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';

const BATCH_STATUS = {
  available: { label: 'Disponible', variant: 'success' },
  depleted: { label: 'Agotado', variant: 'secondary' },
  expired: { label: 'Vencido', variant: 'dark' },
  quarantine: { label: 'Cuarentena', variant: 'warning' },
  damaged: { label: 'Dañado', variant: 'danger' },
};

const ADJUSTMENT_REASONS = [
  { value: 'loss', label: 'Pérdida' },
  { value: 'damage', label: 'Daño / Merma' },
  { value: 'correction', label: 'Corrección de inventario' },
  { value: 'return', label: 'Devolución a proveedor' },
];

const STATUS_TRANSITIONS = [
  { value: 'available', label: 'Disponible', icon: 'bi-check-circle' },
  { value: 'quarantine', label: 'Cuarentena', icon: 'bi-shield-exclamation' },
  { value: 'damaged', label: 'Dañado', icon: 'bi-x-octagon' },
  { value: 'expired', label: 'Vencido', icon: 'bi-calendar-x' },
];

const BatchList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const canEdit = isAdmin || isTechnician;

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    quantity: '',
    reason: 'loss',
    notes: '',
    projectId: '',
  });
  const [adjustError, setAdjustError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Proyectos: sólo hacen falta cuando el ajuste es negativo, porque la base
  // exige `project_id` en toda salida de stock (`valid_exit_movement`).
  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-batch-adjust'],
    queryFn: () => listProjects({ status: PROJECT_STATUS.IN_PROGRESS, pageSize: 100 }),
    enabled: showAdjustModal,
    staleTime: 1000 * 60 * 5,
  });
  const projectOptions = projectsData?.data || [];

  // Fetch product info
  const { data: itemData, isLoading: itemLoading } = useQuery({
    queryKey: ['inventory-item', id],
    queryFn: () => getInventoryItem(id),
    enabled: !!id,
  });

  // Fetch batches
  const { data: batchesData, isLoading: batchesLoading, refetch } = useQuery({
    queryKey: ['inventory-batches', id],
    queryFn: () => getInventoryBatches(id),
    enabled: !!id,
  });

  const product = itemData?.data;
  const batches = useMemo(() => batchesData?.data || [], [batchesData]);

  // Filter batches by status
  const filteredBatches = useMemo(() => {
    if (statusFilter === 'all') return batches;
    if (statusFilter === 'active') {
      return batches.filter((b) => b.status === 'available' && b.quantity_available > 0);
    }
    if (statusFilter === 'depleted') {
      return batches.filter((b) => b.status === 'depleted' || b.quantity_available === 0);
    }
    if (statusFilter === 'damaged') {
      return batches.filter((b) => b.status === 'damaged' || b.status === 'quarantine');
    }
    return batches.filter((b) => b.status === statusFilter);
  }, [batches, statusFilter]);

  // Stats summary
  const stats = useMemo(() => {
    const totalBatches = batches.length;
    const totalAvailable = batches.reduce((sum, b) => sum + Number(b.quantity_available || 0), 0);
    const totalValue = batches.reduce(
      (sum, b) => sum + Number(b.quantity_available || 0) * Number(b.unit_cost || 0),
      0,
    );
    const activeBatches = batches.filter(
      (b) => b.status === 'available' && b.quantity_available > 0,
    ).length;
    return { totalBatches, totalAvailable, totalValue, activeBatches };
  }, [batches]);

  const handleOpenAdjust = (batch) => {
    setSelectedBatch(batch);
    setAdjustForm({ quantity: '', reason: 'loss', notes: '', projectId: '' });
    setAdjustError(null);
    setShowAdjustModal(true);
  };

  const handleCloseAdjust = () => {
    if (isSubmitting) return;
    setShowAdjustModal(false);
    setSelectedBatch(null);
  };

  const handleSubmitAdjust = async (e) => {
    e.preventDefault();
    setAdjustError(null);

    const qty = Number(adjustForm.quantity);
    if (!qty || Number.isNaN(qty)) {
      setAdjustError('Ingrese una cantidad válida (positiva o negativa).');
      return;
    }

    // La base no acepta salidas de stock sin proyecto (`valid_exit_movement`).
    // Se avisa acá, antes de tocar nada, en vez de dejar que reviente el
    // INSERT del movimiento con el nombre crudo del constraint.
    if (qty < 0 && !adjustForm.projectId) {
      setAdjustError('Elegí el proyecto al que se carga esta salida de stock.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await adjustBatch(selectedBatch.id, {
        quantity: qty,
        reason: adjustForm.reason,
        notes: adjustForm.notes,
        projectId: adjustForm.projectId || null,
      });
      if (error) throw error;
      setShowAdjustModal(false);
      setSelectedBatch(null);
      refetch();
    } catch (err) {
      setAdjustError(err.message || 'Error al ajustar el lote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStatus = async (batch, newStatus) => {
    if (batch.status === newStatus) return;
    try {
      const { error } = await supabase
        .from('inventory_batches')
        .update({ status: newStatus })
        .eq('id', batch.id);
      if (error) throw error;
      refetch();
    } catch (err) {
      console.error('Change status error:', err);
      alert(`Error al cambiar estado: ${err.message || err}`);
    }
  };

  const handleViewMovements = (batch) => {
    alert(
      `Próximamente: historial de movimientos del lote ${batch.lot_number || batch.id.slice(0, 8)}.`,
    );
  };

  const getStatusBadge = (status) => {
    const cfg = BATCH_STATUS[status] || { label: status, variant: 'secondary' };
    return <Badge bg={cfg.variant}>{cfg.label}</Badge>;
  };

  // #617: tenia DOS defaults 'USD' encadenados (el del parametro y un `||`)
  // sobre una tabla cuyos lotes son colones y que ademas TIENE columna
  // `currency`. Ahora el rotulo sale del lote o no sale.
  const formatBatchCurrency = (amount, currency) =>
    formatCurrency(Number(amount || 0), currency);

  if (itemLoading || batchesLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando lotes...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <nav aria-label="breadcrumb" className="small mb-1">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        onClick={() => navigate('/inventario')}
                      >
                        Inventario
                      </Button>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Lotes
                    </li>
                  </ol>
                </nav>
                <h1 className="h3 mb-0">{product?.name || 'Producto'}</h1>
                {product?.sku && (
                  <code className="text-primary">{product.sku}</code>
                )}
                <p className="text-muted mb-0 mt-1">
                  {batches.length} lote{batches.length !== 1 ? 's' : ''} registrado{batches.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="d-flex gap-2">
                {canEdit && (
                  <Button
                    variant="primary"
                    onClick={() => navigate('/compras/nueva', { state: { itemId: id, itemName: product?.name } })}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Registrar Compra
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/inventario')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Volver al Inventario
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 rounded p-3">
                      <i className="bi bi-layers fs-4 text-primary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Total Lotes</p>
                    <h3 className="mb-0">{formatNumber(stats.totalBatches)}</h3>
                    <small className="text-muted">{formatNumber(stats.activeBatches)} activos</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 rounded p-3">
                      <i className="bi bi-box-seam fs-4 text-success"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Cantidad Disponible</p>
                    <h3 className="mb-0">
                      {formatNumber(stats.totalAvailable)} {product?.unit || ''}
                    </h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-info bg-opacity-10 rounded p-3">
                      <i className="bi bi-stack fs-4 text-info"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Valor FIFO Total</p>
                    <h3 className="mb-0 fs-5">{formatCurrency(stats.totalValue)}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 rounded p-3">
                      <i className="bi bi-tags fs-4 text-warning"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Precio de Lista</p>
                    <h3 className="mb-0 fs-5">
                      {product?.sale_price ? formatCurrency(product.sale_price) : '-'}
                    </h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters + ViewToggle */}
        <Row className="mb-3 g-2 align-items-end">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Filtrar por estado</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="sm"
              >
                <option value="all">Todos</option>
                <option value="active">Disponibles</option>
                <option value="depleted">Agotados</option>
                <option value="damaged">Dañados / Cuarentena</option>
                <option value="expired">Vencidos</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={8} className="d-flex justify-content-md-end">
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </Col>
        </Row>

        {/* Empty state */}
        {filteredBatches.length === 0 ? (
          <Card className="shadow-sm">
            <Card.Body className="p-5 text-center text-muted">
              <i className="bi bi-layers fs-1 d-block mb-3"></i>
              <p className="mb-2">
                {batches.length === 0
                  ? 'Este producto aún no tiene lotes registrados'
                  : 'No hay lotes con el filtro aplicado'}
              </p>
              {batches.length === 0 && canEdit && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/compras/nueva', { state: { itemId: id, itemName: product?.name } })}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Registrar Compra
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : isTableView ? (
          /* Table view (desktop default) */
          <Row>
            <Col>
              <Card className="shadow-sm">
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th># Lote</th>
                          <th>Fecha Compra</th>
                          <th>Proveedor</th>
                          <th className="text-end">Qty Disponible</th>
                          {/* admin-only: purchase cost */}
                          {isAdmin && <th className="text-end">Costo Unitario</th>}
                          {isAdmin && <th className="text-end">Valor Total</th>}
                          <th>Vencimiento</th>
                          <th>Estado</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBatches.map((batch) => {
                          const totalValue = Number(batch.quantity_available || 0) * Number(batch.unit_cost || 0);
                          return (
                            <tr key={batch.id}>
                              <td>
                                <code className="text-primary">
                                  {batch.lot_number || `LOT-${batch.id.slice(0, 8)}`}
                                </code>
                                {batch.purchase?.invoice_number && (
                                  <small className="d-block text-muted">
                                    Fact: {batch.purchase.invoice_number}
                                  </small>
                                )}
                              </td>
                              <td>{formatDate(batch.created_at)}</td>
                              {/* El proveedor cuelga de la COMPRA
                                  (inventory_batches → purchases → suppliers),
                                  que es como lo trae el select del servicio.
                                  `batch.supplier` no existe: leerlo hacía que
                                  todos los lotes dijeran "Sin proveedor". */}
                              <td>{batch.purchase?.supplier?.name || <span className="text-muted">-</span>}</td>
                              <td className="text-end">
                                <strong>{formatNumber(batch.quantity_available)}</strong>
                                <small className="text-muted d-block">
                                  de {formatNumber(batch.quantity_received)}
                                </small>
                              </td>
                              {/* admin-only: purchase cost */}
                              {isAdmin && (
                                <td className="text-end">
                                  {formatBatchCurrency(batch.unit_cost, batch.currency)}
                                </td>
                              )}
                              {isAdmin && (
                                <td className="text-end">
                                  <strong>{formatBatchCurrency(totalValue, batch.currency)}</strong>
                                </td>
                              )}
                              <td>
                                {batch.expiration_date
                                  ? formatDate(batch.expiration_date)
                                  : <span className="text-muted">-</span>}
                              </td>
                              <td>{getStatusBadge(batch.status)}</td>
                              <td className="text-center">
                                <KebabMenu
                                  label={`Acciones del lote ${batch.lot_number || `LOT-${batch.id.slice(0, 8)}`}`}
                                  stopPropagation={false}
                                >
                                    {canEdit && (
                                      <>
                                        <Dropdown.Item onClick={() => handleOpenAdjust(batch)}>
                                          <i className="bi bi-arrow-left-right me-2"></i>
                                          Ajustar Cantidad
                                        </Dropdown.Item>
                                        <Dropdown.Header>Cambiar Estado</Dropdown.Header>
                                        {STATUS_TRANSITIONS.map((s) => (
                                          <Dropdown.Item
                                            key={s.value}
                                            disabled={batch.status === s.value}
                                            onClick={() => handleChangeStatus(batch, s.value)}
                                          >
                                            <i className={`bi ${s.icon} me-2`}></i>
                                            {s.label}
                                          </Dropdown.Item>
                                        ))}
                                        <Dropdown.Divider />
                                      </>
                                    )}
                                    <Dropdown.Item onClick={() => handleViewMovements(batch)}>
                                      <i className="bi bi-clock-history me-2"></i>
                                      Ver Movimientos
                                    </Dropdown.Item>
                                </KebabMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          /* Card view (mobile default) */
          <Row className="g-3">
            {filteredBatches.map((batch) => {
              const totalValue = Number(batch.quantity_available || 0) * Number(batch.unit_cost || 0);
              return (
                <Col xs={12} sm={6} lg={4} key={batch.id}>
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h6 className="mb-1">
                            <code className="text-primary">
                              {batch.lot_number || `LOT-${batch.id.slice(0, 8)}`}
                            </code>
                          </h6>
                          <small className="text-muted">{formatDate(batch.created_at)}</small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {getStatusBadge(batch.status)}
                          <KebabMenu
                            label={`Acciones del lote ${batch.lot_number || `LOT-${batch.id.slice(0, 8)}`}`}
                            stopPropagation={false}
                          >
                              {canEdit && (
                                <>
                                  <Dropdown.Item onClick={() => handleOpenAdjust(batch)}>
                                    <i className="bi bi-arrow-left-right me-2"></i>
                                    Ajustar Cantidad
                                  </Dropdown.Item>
                                  <Dropdown.Header>Cambiar Estado</Dropdown.Header>
                                  {STATUS_TRANSITIONS.map((s) => (
                                    <Dropdown.Item
                                      key={s.value}
                                      disabled={batch.status === s.value}
                                      onClick={() => handleChangeStatus(batch, s.value)}
                                    >
                                      <i className={`bi ${s.icon} me-2`}></i>
                                      {s.label}
                                    </Dropdown.Item>
                                  ))}
                                  <Dropdown.Divider />
                                </>
                              )}
                              <Dropdown.Item onClick={() => handleViewMovements(batch)}>
                                <i className="bi bi-clock-history me-2"></i>
                                Ver Movimientos
                              </Dropdown.Item>
                          </KebabMenu>
                        </div>
                      </div>

                      <Row className="g-2 mb-2">
                        <Col xs={6}>
                          <small className="text-muted d-block">Disponible</small>
                          <strong>
                            {formatNumber(batch.quantity_available)} {product?.unit || ''}
                          </strong>
                          <small className="text-muted d-block">
                            de {formatNumber(batch.quantity_received)}
                          </small>
                        </Col>
                        {/* admin-only: purchase cost */}
                        {isAdmin && (
                          <Col xs={6}>
                            <small className="text-muted d-block">Costo Unit.</small>
                            <strong>{formatBatchCurrency(batch.unit_cost, batch.currency)}</strong>
                          </Col>
                        )}
                        {isAdmin && (
                          <Col xs={6}>
                            <small className="text-muted d-block">Valor Total</small>
                            <strong className="text-primary">
                              {formatBatchCurrency(totalValue, batch.currency)}
                            </strong>
                          </Col>
                        )}
                        <Col xs={6}>
                          <small className="text-muted d-block">Vencimiento</small>
                          <strong>
                            {batch.expiration_date ? formatDate(batch.expiration_date) : '-'}
                          </strong>
                        </Col>
                      </Row>

                      <div className="border-top pt-2 mt-2">
                        <small className="text-muted d-block">
                          <i className="bi bi-truck me-1"></i>
                          {/* Ver la nota de la tabla: el proveedor viene por purchase. */}
                          {batch.purchase?.supplier?.name || 'Sin proveedor'}
                          {batch.purchase?.invoice_number && ` · Fact. ${batch.purchase.invoice_number}`}
                        </small>
                        {batch.location && (
                          <small className="text-muted d-block">
                            <i className="bi bi-geo-alt me-1"></i>
                            {batch.location}
                          </small>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Adjust Quantity Modal */}
        <Modal show={showAdjustModal} onHide={handleCloseAdjust} centered>
          <Form onSubmit={handleSubmitAdjust}>
            <Modal.Header closeButton>
              <Modal.Title>Ajustar Cantidad de Lote</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedBatch && (
                <div className="mb-3 small text-muted">
                  Lote:{' '}
                  <code className="text-primary">
                    {selectedBatch.lot_number || `LOT-${selectedBatch.id.slice(0, 8)}`}
                  </code>
                  <br />
                  Disponible actual:{' '}
                  <strong>
                    {formatNumber(selectedBatch.quantity_available)} {product?.unit || ''}
                  </strong>
                </div>
              )}

              {adjustError && (
                <Alert variant="danger" className="py-2 small">
                  {adjustError}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Cantidad de ajuste</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={adjustForm.quantity}
                  onChange={(e) =>
                    setAdjustForm({ ...adjustForm, quantity: e.target.value })
                  }
                  placeholder="Ej: -2 (merma) o 5 (corrección positiva)"
                  required
                />
                <Form.Text className="text-muted">
                  Use valores negativos para mermas o pérdidas, positivos para correcciones.
                  Toda baja se registra como salida y hay que decir a qué proyecto se carga.
                </Form.Text>
              </Form.Group>

              {Number(adjustForm.quantity) < 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Proyecto al que se carga <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={adjustForm.projectId}
                    onChange={(e) =>
                      setAdjustForm({ ...adjustForm, projectId: e.target.value })
                    }
                  >
                    <option value="">Seleccionar proyecto...</option>
                    {projectOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title || p.project_number}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    La base exige atribuir cada salida de stock a un proyecto
                    (constraint <code>valid_exit_movement</code>).
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Motivo</Form.Label>
                <Form.Select
                  value={adjustForm.reason}
                  onChange={(e) =>
                    setAdjustForm({ ...adjustForm, reason: e.target.value })
                  }
                >
                  {ADJUSTMENT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Notas (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adjustForm.notes}
                  onChange={(e) =>
                    setAdjustForm({ ...adjustForm, notes: e.target.value })
                  }
                  placeholder="Detalles adicionales del ajuste..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={handleCloseAdjust}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Ajustando...
                  </>
                ) : (
                  'Confirmar Ajuste'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default BatchList;
